import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Ban, CircleCheck, LogOut } from "lucide-react";
import { ShellLayout } from "@/components/shell/ShellLayout";
import { Spinner } from "@/components/common/FullScreenLoader";
import { BoolBadge, TypeBadge } from "@/components/shell/Badges";
import { SectionHeading } from "@/components/shell/StatCard";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { formatDateTime } from "@/lib/format";

function DisableRow({ user, onCancel, onConfirm, busy }) {
  const [reason, setReason] = useState("");
  return (
    <tr className="bg-red-500/5" data-testid={`people-disable-confirm-${user.id}`}>
      <td className="bz-td" colSpan={7}>
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm text-red-200">Disable {user.email}? All sessions will be revoked immediately.</span>
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional)" maxLength={280} className="bz-input max-w-xs py-1.5 text-xs" data-testid={`people-disable-reason-${user.id}`} />
          <button type="button" onClick={() => onConfirm(reason)} disabled={busy} className="bz-btn-danger text-xs" data-testid={`people-disable-confirm-btn-${user.id}`}>
            {busy ? <Spinner className="h-3.5 w-3.5" /> : <Ban className="h-3.5 w-3.5" strokeWidth={1.75} />} Confirm disable
          </button>
          <button type="button" onClick={onCancel} className="bz-btn-ghost text-xs" data-testid={`people-disable-cancel-btn-${user.id}`}>
            Cancel
          </button>
        </div>
      </td>
    </tr>
  );
}

export default function HubPeople() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState(null);
  const [confirming, setConfirming] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (e) {
      toast.error(apiError(e).message);
      setUsers([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function act(user, action, body) {
    setBusyId(user.id);
    try {
      await api.post(`/admin/users/${user.id}/${action}`, body ?? {});
      toast.success(action === "disable" ? `${user.email} disabled` : action === "enable" ? `${user.email} re-enabled` : `Sessions revoked for ${user.email}`);
      setConfirming(null);
      await load();
    } catch (e) {
      toast.error(apiError(e).message);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <ShellLayout area="hub" eyebrow="Network Owner" title="People">
      <div data-testid="hub-people-page" className="space-y-6">
        <SectionHeading title="All identities" description="Disable accounts, re-enable them, or force sign-out. Every action is written to the audit ledger." />
        {users === null ? (
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <Spinner className="h-4 w-4 text-electric" /> Loading people…
          </div>
        ) : (
          <div className="bz-table-wrap">
            <table data-testid="people-users-table" className="w-full">
              <thead>
                <tr>
                  <th className="bz-th">Identity</th>
                  <th className="bz-th">Type</th>
                  <th className="bz-th">MFA</th>
                  <th className="bz-th">Email</th>
                  <th className="bz-th">Sessions</th>
                  <th className="bz-th">Status</th>
                  <th className="bz-th text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <PeopleRows key={u.id} u={u} me={me} confirming={confirming} setConfirming={setConfirming} busyId={busyId} act={act} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </ShellLayout>
  );
}

function PeopleRows({ u, me, confirming, setConfirming, busyId, act }) {
  const isMe = u.id === me.id;
  return (
    <>
      <tr className="bz-tr" data-testid={`people-row-${u.id}`}>
        <td className="bz-td">
          <p className="text-white">{u.display_name}</p>
          <p className="font-mono text-[11px] text-slate-500">
            {u.email}
            {isMe ? <span className="ml-2 text-electric">· you</span> : null}
          </p>
          <p className="text-[11px] text-slate-600">Created {formatDateTime(u.created_at)}</p>
        </td>
        <td className="bz-td">
          <TypeBadge user={u} />
        </td>
        <td className="bz-td">
          <BoolBadge value={u.mfa_enabled} yes="On" no="Off" />
        </td>
        <td className="bz-td">
          <BoolBadge value={u.email_verified} yes="Verified" no="Unverified" />
        </td>
        <td className="bz-td font-mono">{u.active_sessions}</td>
        <td className="bz-td">
          <BoolBadge value={!u.is_disabled} yes="Active" no="Disabled" testId={`people-status-${u.id}`} />
        </td>
        <td className="bz-td">
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => act(u, "sessions/revoke")} disabled={busyId === u.id || u.active_sessions === 0} className="bz-btn-secondary text-xs" data-testid={`people-revoke-sessions-btn-${u.id}`}>
              <LogOut className="h-3.5 w-3.5" strokeWidth={1.75} /> Revoke sessions
            </button>
            {u.is_disabled ? (
              <button type="button" onClick={() => act(u, "enable")} disabled={busyId === u.id} className="bz-btn-secondary text-xs" data-testid={`people-toggle-user-btn-${u.id}`}>
                {busyId === u.id ? <Spinner className="h-3.5 w-3.5" /> : <CircleCheck className="h-3.5 w-3.5 text-emerald-300" strokeWidth={1.75} />} Enable
              </button>
            ) : (
              <button type="button" onClick={() => setConfirming(u.id)} disabled={isMe || busyId === u.id} title={isMe ? "You cannot disable your own account" : undefined} className="bz-btn-danger text-xs" data-testid={`people-toggle-user-btn-${u.id}`}>
                <Ban className="h-3.5 w-3.5" strokeWidth={1.75} /> Disable
              </button>
            )}
          </div>
        </td>
      </tr>
      {confirming === u.id ? <DisableRow user={u} busy={busyId === u.id} onCancel={() => setConfirming(null)} onConfirm={(reason) => act(u, "disable", { reason: reason || null })} /> : null}
    </>
  );
}
